import React, { useState, useEffect } from 'react';
import { FaUser, FaMobileAlt, FaCalendarAlt, FaCrown, FaRupeeSign, FaHistory, FaEdit, FaSave, FaTimes, FaGift, FaPaperPlane, FaTrash, FaEnvelope, FaFingerprint, FaUsers } from 'react-icons/fa';
import { MdBlock, MdCheckCircle, MdVerified } from 'react-icons/md';
import { LuDot } from 'react-icons/lu';
import { HiOutlineDeviceMobile } from 'react-icons/hi';
import { BiTime } from 'react-icons/bi';
import { FiAward, FiClock, FiUsers, FiUsers as FiUsersIcon } from 'react-icons/fi';
import Loader from '../../components/Loader';
import User_Navbar from './User_Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import generateCaptcha from '../../components/captcha';
import UserReferralHistory from './UserReferralHistory';
import UserReferralHistoryPlan from './UserReferralHistoryPlan';

const UserInfo = () => {
    const [loader, setLoader] = useState(false);
    const [userData, setUserData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({
        u_name: '',
        u_ref_count: 0,
        search_limit: 0
    });
    const [planCounts, setPlanCounts] = useState({ totalPlanPurchases: 0, totalReferralPlans: 0 });
    const { id } = useParams();
    const navigate = useNavigate();

    // Send Admin Plan Modal States
    const [showSendPlanModal, setShowSendPlanModal] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [sendingPlan, setSendingPlan] = useState(false);

    // View User History Modal States
    const [showUserHistoryModal, setShowUserHistoryModal] = useState(false);
    const [userHistoryData, setUserHistoryData] = useState([]);
    const [loadingUserHistory, setLoadingUserHistory] = useState(false);

    // View Referral History Modal State
    const [showReferralHistory, setShowReferralHistory] = useState(false);

    // View Referral Plan History Modal State
    const [showReferralPlanHistory, setShowReferralPlanHistory] = useState(false);

    // Fetch user data
    const fetchUserData = async () => {
        setLoader(true);
        try {
            const response = await Calling.userInfo(id);
            if (response?.status) {
                setUserData(response?.data);
                setPlanCounts(response?.planCounts || { totalPlanPurchases: 0, totalReferralPlans: 0 });
                setEditedData({
                    u_name: response?.data?.u_name || '',
                    u_ref_count: response?.data?.u_ref_count || 0,
                    search_limit: response?.data?.search_limit || 0
                });
            } else {
                toast.error(response?.message || 'Failed to fetch user data.');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setLoader(false);
        }
    };

    // Fetch admin plans
    const fetchPlans = async () => {
        setLoadingPlans(true);
        try {
            const response = await Calling.getPlan();
            if (response?.status) {
                setPlans(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to load plans');
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load plans');
        } finally {
            setLoadingPlans(false);
        }
    };

    // Fetch user purchase history
    const fetchUserHistory = async () => {
        setLoadingUserHistory(true);
        try {
            const response = await Calling.getPlanHistory('', '', '', '', id);
            if (response?.status) {
                setUserHistoryData(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to load user history');
            }
        } catch (error) {
            console.error('Error fetching user history:', error);
            toast.error('Failed to load user history');
        } finally {
            setLoadingUserHistory(false);
        }
    };

    // Send admin plan to user
    const handleSendAdminPlan = async () => {
        if (!selectedPlan) {
            toast.error('Please select a plan');
            return;
        }

        const content = `You are about to send a plan to "${userData?.u_name}". To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            setSendingPlan(true);
            try {
                const response = await Calling.sendAdminPlan({
                    planId: selectedPlan,
                    userId: id
                });

                if (response?.status) {
                    toast.success(response?.message || 'Plan sent successfully');
                    setShowSendPlanModal(false);
                    setSelectedPlan('');
                    fetchUserData();
                } else {
                    toast.error(response?.message || 'Failed to send plan');
                }
            } catch (error) {
                console.error('Error sending plan:', error);
                toast.error('Failed to send plan');
            } finally {
                setSendingPlan(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    // Delete user history
    const handleDeleteHistory = async (historyId) => {
        const content = `You are about to delete this purchase history. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                const response = await Calling.detelePurchasePlan(historyId);
                if (response?.status) {
                    toast.success(response?.message || 'History deleted successfully');
                    fetchUserHistory();
                } else {
                    toast.error(response?.message || 'Failed to delete history');
                }
            } catch (error) {
                console.error('Error deleting history:', error);
                toast.error('Failed to delete history');
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    useEffect(() => {
        fetchUserData();
        // eslint-disable-next-line
    }, [id]);

    // Handle input change for editable fields
    const handleInputChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Save changes
    const handleSaveChanges = async () => {
        setLoader(true);
        try {
            const body = {
                u_name: editedData.u_name,
                u_ref_count: parseInt(editedData.u_ref_count) || 0,
                search_limit: parseInt(editedData.search_limit) || 0
            };
            const response = await Calling.updateUserInfo(id, body);
            if (response?.status) {
                toast.success(response?.message || 'User updated successfully!');
                setIsEditing(false);
                fetchUserData();
            } else {
                toast.error(response?.message || 'Failed to save changes.');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoader(false);
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedData({
            u_name: userData?.u_name || '',
            u_ref_count: userData?.u_ref_count || 0,
            search_limit: userData?.search_limit || 0
        });
    };

    // Block/unblock user
    const handleBlockUser = async (userId, status) => {
        const content = `You are about to block this user. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        setLoader(true);
        try {
            const response = await Calling.blockUser(userId, status);
            if (response?.status) {
                toast.success(response?.message || 'User status updated!');
            } else {
                toast.error(response?.message || 'Failed to update user status.');
            }
        } catch (error) {
            console.error('Error updating user status:', error);
        } finally {
            setLoader(false);
            fetchUserData();
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 text-xs rounded-full bg-green-100 text-green-600 flex items-center gap-1'><LuDot size={16} /> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 text-xs rounded-full bg-red-100 text-red-600 flex items-center gap-1'><LuDot size={16} /> Expired</span>;
            case 'cancelled':
                return <span className='px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-600 flex items-center gap-1'><LuDot size={16} /> Cancelled</span>;
            default:
                return <span className='px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 flex items-center gap-1'><LuDot size={16} /> {status}</span>;
        }
    };

    // Calculate days remaining for referral plan
    const getReferralPlanDaysRemaining = () => {
        if (!userData?.active_ref_plan_end_date) return 0;
        const endDate = new Date(userData.active_ref_plan_end_date);
        const today = new Date();
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    return (
        <>
            {loader ? (
                <Loader />
            ) : (
                <div className="w-full text-main-font">
                    {/* Page Header */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                                <FiUsers size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                                <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                            </div>
                        </div>
                    </div>
                    <div className='bg-main-white rounded-xl'>
                        <div className='rounded-xl'>
                            <User_Navbar selectState={'user'} />
                            <hr className='border-main-border' />

                            <div className='p-6'>
                                {/* Header Section */}
                                <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6'>
                                    <div className='flex items-center gap-3'>
                                        <h1 className='text-xl md:text-2xl font-bold text-main-font'>
                                            User Details
                                        </h1>
                                        {isEditing && (
                                            <span className='px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-600 font-medium animate-pulse'>
                                                Editing Mode
                                            </span>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className='flex flex-wrap gap-2'>
                                        {!userData?.has_active_plan && (
                                            <button
                                                onClick={() => {
                                                    setShowSendPlanModal(true);
                                                    fetchPlans();
                                                }}
                                                className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg'
                                            >
                                                <FaGift size={16} />
                                                <span className='font-medium'>Send Plan</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowUserHistoryModal(true);
                                                fetchUserHistory();
                                            }}
                                            className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg'
                                        >
                                            <FaHistory size={16} />
                                            <span className='font-medium'>Transactions</span>
                                        </button>
                                        <button
                                            onClick={() => setShowReferralHistory(true)}
                                            className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all shadow-md hover:shadow-lg'
                                        >
                                            <FaUsers size={16} />
                                            <span className='font-medium'>Referrals</span>
                                        </button>
                                        <button
                                            onClick={() => setShowReferralPlanHistory(true)}
                                            className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg'
                                        >
                                            <FaGift size={16} />
                                            <span className='font-medium'>Ref Plans</span>
                                        </button>
                                    </div>
                                </div>

                                {userData && (
                                    <div className='grid grid-cols-1 xl:grid-cols-3 gap-6'>
                                        {/* Main User Card */}
                                        <div className='xl:col-span-2 space-y-6'>
                                            {/* Profile Card */}
                                            <div className='bg-gradient-to-br from-main-blue/5 to-blue-100/30 rounded-2xl p-6 border border-main-border'>
                                                <div className='flex flex-col sm:flex-row items-start sm:items-center gap-5'>
                                                    {/* Avatar */}
                                                    <div className='relative'>
                                                        <div className='w-20 h-20 bg-gradient-to-br from-main-blue to-blue-600 rounded-2xl flex items-center justify-center shadow-lg'>
                                                            {userData.u_profile ? <img src={userData.u_profile} alt="Profile" className='w-full h-full object-cover rounded-2xl' /> : <FaUser className='text-white' size={32} />}
                                                        </div>
                                                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${userData.u_status ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                                    </div>

                                                    {/* Name & Status */}
                                                    <div className='flex-1'>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editedData.u_name}
                                                                onChange={(e) => handleInputChange('u_name', e.target.value)}
                                                                className="text-2xl font-bold text-main-font bg-white border-2 border-main-blue rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-main-blue/50 w-full max-w-md"
                                                                placeholder="Enter user name"
                                                            />
                                                        ) : (
                                                            <h2 className='text-2xl font-bold text-main-font flex items-center gap-2'>
                                                                {userData.u_name || 'N/A'}
                                                                {userData.u_status && <MdVerified className='text-main-blue' size={22} />}
                                                            </h2>
                                                        )}
                                                        <p className='text-main-subfont mt-1'>User ID: {userData._id}</p>
                                                        <div className='flex flex-wrap items-center gap-2 mt-3'>
                                                            {userData.u_status ? (
                                                                <span className='px-3 py-1.5 text-sm rounded-full bg-green-100 text-green-600 flex items-center gap-1 font-medium'>
                                                                    <span className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></span>
                                                                    Active
                                                                </span>
                                                            ) : (
                                                                <span className='px-3 py-1.5 text-sm rounded-full bg-red-100 text-red-600 flex items-center gap-1 font-medium'>
                                                                    <span className='w-2 h-2 bg-red-500 rounded-full'></span>
                                                                    Blocked
                                                                </span>
                                                            )}
                                                            {userData.has_active_plan && (
                                                                <span className='px-3 py-1.5 text-sm rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1 font-medium'>
                                                                    <FaCrown size={12} />
                                                                    Premium
                                                                </span>
                                                            )}
                                                            {userData.has_active_ref_plan && (
                                                                <span className='px-3 py-1.5 text-sm rounded-full bg-blue-100 text-blue-700 flex items-center gap-1 font-medium'>
                                                                    <FaUsers size={12} />
                                                                    Referral Plan
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Edit/Save Buttons */}
                                                    <div className='flex gap-2'>
                                                        {isEditing ? (
                                                            <>
                                                                <button
                                                                    onClick={handleSaveChanges}
                                                                    className='p-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors shadow-md'
                                                                    title='Save'
                                                                >
                                                                    <FaSave size={18} />
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    className='p-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors shadow-md'
                                                                    title='Cancel'
                                                                >
                                                                    <FaTimes size={18} />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() => setIsEditing(true)}
                                                                className='p-3 bg-main-blue text-white rounded-xl hover:bg-blue-600 transition-colors shadow-md'
                                                                title='Edit'
                                                            >
                                                                <FaEdit size={18} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Info Grid */}
                                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                                {/* Email Info */}
                                                <div className='bg-main-th rounded-2xl p-5 border border-main-border hover:shadow-md transition-shadow'>
                                                    <div className='flex items-center gap-3 mb-3'>
                                                        <div className='p-2.5 bg-sky-100 rounded-xl'>
                                                            <FaEnvelope className='text-sky-600' size={20} />
                                                        </div>
                                                        <span className='text-main-subfont font-medium'>Email</span>
                                                    </div>
                                                    <p className='text-lg font-semibold text-main-font break-all'>{userData.u_email || 'N/A'}</p>
                                                </div>

                                                {/* Device Info */}
                                                <div className='bg-main-th rounded-2xl p-5 border border-main-border hover:shadow-md transition-shadow'>
                                                    <div className='flex items-center gap-3 mb-3'>
                                                        <div className='p-2.5 bg-purple-100 rounded-xl'>
                                                            <HiOutlineDeviceMobile className='text-purple-600' size={22} />
                                                        </div>
                                                        <span className='text-main-subfont font-medium'>Device</span>
                                                    </div>
                                                    <p className='text-lg font-semibold text-main-font'>{userData.u_device || 'N/A'}</p>
                                                </div>

                                                {/* Created Date */}
                                                <div className='bg-main-th rounded-2xl p-5 border border-main-border hover:shadow-md transition-shadow'>
                                                    <div className='flex items-center gap-3 mb-3'>
                                                        <div className='p-2.5 bg-blue-100 rounded-xl'>
                                                            <FaCalendarAlt className='text-blue-600' size={20} />
                                                        </div>
                                                        <span className='text-main-subfont font-medium'>Joined Date</span>
                                                    </div>
                                                    <p className='text-lg font-semibold text-main-font'>{formatDate(userData.createdAt)}</p>
                                                </div>

                                                {/* Referral Code */}
                                                <div className='bg-main-th rounded-2xl p-5 border border-main-border hover:shadow-md transition-shadow'>
                                                    <div className='flex items-center gap-3 mb-3'>
                                                        <div className='p-2.5 bg-indigo-100 rounded-xl'>
                                                            <FaFingerprint className='text-indigo-600' size={20} />
                                                        </div>
                                                        <span className='text-main-subfont font-medium'>Referral Code</span>
                                                    </div>
                                                    <p className='text-lg font-semibold text-main-font tracking-wide'>{userData.u_ref_code || 'N/A'}</p>
                                                </div>

                                                {/* Referral Count - Editable */}
                                                <div className={`bg-main-th rounded-2xl p-5 border hover:shadow-md transition-shadow ${isEditing ? 'border-pink-300 bg-pink-50/30' : 'border-main-border'}`}>
                                                    <div className='flex items-center justify-between mb-3'>
                                                        <div className='flex items-center gap-3'>
                                                            <div className='p-2.5 bg-pink-100 rounded-xl'>
                                                                <FaUsers className='text-pink-600' size={20} />
                                                            </div>
                                                            <span className='text-main-subfont font-medium'>Total Referrals</span>
                                                        </div>
                                                        {isEditing && (
                                                            <span className='text-xs text-pink-500 font-medium bg-pink-100 px-2 py-1 rounded-full'>
                                                                Editable
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={editedData.u_ref_count}
                                                            onChange={(e) => handleInputChange('u_ref_count', e.target.value)}
                                                            className="w-full text-lg font-semibold text-main-font bg-white border-2 border-pink-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                                                            placeholder="Enter referral count"
                                                        />
                                                    ) : (
                                                        <p className='text-lg font-semibold text-main-font'>{userData.u_ref_count || 0}</p>
                                                    )}
                                                </div>

                                                {/* Search Limit - Editable */}
                                                <div className={`bg-main-th rounded-2xl p-5 border hover:shadow-md transition-shadow ${isEditing ? 'border-teal-300 bg-teal-50/30' : 'border-main-border'}`}>
                                                    <div className='flex items-center justify-between mb-3'>
                                                        <div className='flex items-center gap-3'>
                                                            <div className='p-2.5 bg-teal-100 rounded-xl'>
                                                                <FiAward className='text-teal-600' size={20} />
                                                            </div>
                                                            <span className='text-main-subfont font-medium'>Search Limit</span>
                                                        </div>
                                                        {isEditing && (
                                                            <span className='text-xs text-teal-500 font-medium bg-teal-100 px-2 py-1 rounded-full'>
                                                                Editable
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={editedData.search_limit}
                                                            onChange={(e) => handleInputChange('search_limit', e.target.value)}
                                                            className="w-full text-lg font-semibold text-main-font bg-white border-2 border-teal-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                                            placeholder="Enter search limit"
                                                        />
                                                    ) : (
                                                        <p className='text-lg font-semibold text-main-font'>{userData.search_limit || 0}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Side Panel */}
                                        <div className='space-y-6'>
                                            {/* Quick Actions Card */}
                                            <div className='bg-main-th rounded-2xl p-5 border border-main-border'>
                                                <h3 className='text-lg font-semibold text-main-font mb-4 flex items-center gap-2'>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-main-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                    </svg>
                                                    Quick Actions
                                                </h3>
                                                <div className='space-y-3'>
                                                    {/* Block/Unblock Button */}
                                                    <button
                                                        onClick={() => handleBlockUser(userData._id, !userData.u_status)}
                                                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${userData.u_status
                                                            ? 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'
                                                            : 'bg-green-50 hover:bg-green-100 text-green-600 border border-green-200'
                                                            }`}
                                                    >
                                                        <span className='flex items-center gap-2 font-medium'>
                                                            {userData.u_status ? <MdBlock size={20} /> : <MdCheckCircle size={20} />}
                                                            {userData.u_status ? 'Block User' : 'Unblock User'}
                                                        </span>
                                                    </button>

                                                    {/* Send Plan Button */}
                                                    {!userData.has_active_plan && (
                                                        <button
                                                            onClick={() => {
                                                                setShowSendPlanModal(true);
                                                                fetchPlans();
                                                            }}
                                                            className='w-full flex items-center justify-between px-4 py-3 rounded-xl bg-yellow-50 hover:bg-yellow-100 text-yellow-600 border border-yellow-200 transition-all'
                                                        >
                                                            <span className='flex items-center gap-2 font-medium'>
                                                                <FaGift size={18} />
                                                                Send Plan
                                                            </span>
                                                        </button>
                                                    )}

                                                    {/* View History Button */}
                                                    <button
                                                        onClick={() => {
                                                            setShowUserHistoryModal(true);
                                                            fetchUserHistory();
                                                        }}
                                                        className='w-full flex items-center justify-between px-4 py-3 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200 transition-all'
                                                    >
                                                        <span className='flex items-center gap-2 font-medium'>
                                                            <FaHistory size={18} />
                                                            Transactions
                                                        </span>
                                                    </button>

                                                    {/* View Referral History Button */}
                                                    <button
                                                        onClick={() => setShowReferralHistory(true)}
                                                        className='w-full flex items-center justify-between px-4 py-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border border-indigo-200 transition-all'
                                                    >
                                                        <span className='flex items-center gap-2 font-medium'>
                                                            <FaUsers size={18} />
                                                            Referral History
                                                        </span>
                                                    </button>

                                                    {/* View Referral Plan History Button */}
                                                    <button
                                                        onClick={() => setShowReferralPlanHistory(true)}
                                                        className='w-full flex items-center justify-between px-4 py-3 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-600 border border-purple-200 transition-all'
                                                    >
                                                        <span className='flex items-center gap-2 font-medium'>
                                                            <FaGift size={18} />
                                                            Referral Plan History
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Editable Fields Summary (when editing) */}
                                            {isEditing && (
                                                <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200'>
                                                    <h3 className='text-lg font-semibold text-main-font mb-4 flex items-center gap-2'>
                                                        <FaEdit className='text-blue-500' size={18} />
                                                        Editing Summary
                                                    </h3>
                                                    <div className='space-y-3'>
                                                        <div className='flex justify-between items-center text-sm'>
                                                            <span className='text-main-subfont'>Name</span>
                                                            <span className='font-medium text-main-font truncate max-w-[150px]'>{editedData.u_name || 'N/A'}</span>
                                                        </div>
                                                        <div className='flex justify-between items-center text-sm'>
                                                            <span className='text-main-subfont'>Referral Count</span>
                                                            <span className='font-medium text-pink-600'>{editedData.u_ref_count}</span>
                                                        </div>
                                                        <div className='flex justify-between items-center text-sm'>
                                                            <span className='text-main-subfont'>Search Limit</span>
                                                            <span className='font-medium text-teal-600'>{editedData.search_limit}</span>
                                                        </div>
                                                        <hr className='border-blue-200' />
                                                        <div className='flex gap-2'>
                                                            <button
                                                                onClick={handleSaveChanges}
                                                                className='flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors text-sm font-medium'
                                                            >
                                                                <FaSave size={14} />
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                className='flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors text-sm font-medium'
                                                            >
                                                                <FaTimes size={14} />
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Active Premium Plan Info (if exists) */}
                                            {userData.has_active_plan && userData.active_plan_end_date && (
                                                <div className='bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-5 border border-yellow-200'>
                                                    <h3 className='text-lg font-semibold text-main-font mb-4 flex items-center gap-2'>
                                                        <FaCrown className='text-yellow-500' size={20} />
                                                        Active Premium Plan
                                                    </h3>
                                                    <div className='space-y-3'>
                                                        <div className='flex justify-between items-center'>
                                                            <span className='text-main-subfont'>Plan Expires</span>
                                                            <span className='font-semibold text-main-font'>{formatDate(userData.active_plan_end_date)}</span>
                                                        </div>
                                                        <div className='w-full bg-yellow-200 rounded-full h-2'>
                                                            <div className='bg-yellow-500 h-2 rounded-full' style={{ width: '60%' }}></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Active Referral Plan Info (if exists) */}
                                            {userData.has_active_ref_plan && userData.active_ref_plan_end_date && (
                                                <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200'>
                                                    <h3 className='text-lg font-semibold text-main-font mb-4 flex items-center gap-2'>
                                                        <FaUsers className='text-blue-500' size={20} />
                                                        Active Referral Plan
                                                    </h3>
                                                    <div className='space-y-3'>
                                                        <div className='flex justify-between items-center'>
                                                            <span className='text-main-subfont'>Plan Expires</span>
                                                            <span className='font-semibold text-main-font'>{formatDate(userData.active_ref_plan_end_date)}</span>
                                                        </div>
                                                        <div className='flex justify-between items-center'>
                                                            <span className='text-main-subfont'>Days Remaining</span>
                                                            <span className='font-semibold text-blue-600'>{getReferralPlanDaysRemaining()} days</span>
                                                        </div>
                                                        <div className='w-full bg-blue-200 rounded-full h-2'>
                                                            <div
                                                                className='bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all'
                                                                style={{ width: `${Math.min((getReferralPlanDaysRemaining() / 30) * 100, 100)}%` }}
                                                            ></div>
                                                        </div>
                                                        <button
                                                            onClick={() => setShowReferralPlanHistory(true)}
                                                            className='w-full mt-2 px-4 py-2 text-sm bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium'
                                                        >
                                                            View Details
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Plan Status Cards */}
                                            <div className='grid grid-cols-2 gap-3'>
                                                <div className={`rounded-xl p-4 border ${userData.has_active_plan ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className='flex items-center gap-2 mb-1'>
                                                        <FaCrown className={userData.has_active_plan ? 'text-yellow-500' : 'text-gray-400'} size={16} />
                                                        <span className='text-xs font-medium text-gray-500 uppercase'>Premium</span>
                                                    </div>
                                                    <p className={`text-sm font-semibold ${userData.has_active_plan ? 'text-yellow-600' : 'text-gray-500'}`}>
                                                        {userData.has_active_plan ? 'Active' : 'Inactive'}
                                                    </p>
                                                </div>
                                                <div className={`rounded-xl p-4 border ${userData.has_active_ref_plan ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className='flex items-center gap-2 mb-1'>
                                                        <FaUsers className={userData.has_active_ref_plan ? 'text-blue-500' : 'text-gray-400'} size={16} />
                                                        <span className='text-xs font-medium text-gray-500 uppercase'>Referral</span>
                                                    </div>
                                                    <p className={`text-sm font-semibold ${userData.has_active_ref_plan ? 'text-blue-600' : 'text-gray-500'}`}>
                                                        {userData.has_active_ref_plan ? 'Active' : 'Inactive'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Total Purchase Counts */}
                                            <div className='bg-main-th rounded-2xl p-5 border border-main-border'>
                                                <h3 className='text-lg font-semibold text-main-font mb-4 flex items-center gap-2'>
                                                    <FaHistory className='text-main-blue' size={18} />
                                                    Total Purchases
                                                </h3>
                                                <div className='space-y-3'>
                                                    <div
                                                        onClick={() => {
                                                            setShowUserHistoryModal(true);
                                                            fetchUserHistory();
                                                        }}
                                                        className='flex items-center justify-between p-3 bg-yellow-50 rounded-xl border border-yellow-200 cursor-pointer hover:shadow-md transition-all'
                                                    >
                                                        <div className='flex items-center gap-3'>
                                                            <div className='p-2 bg-yellow-100 rounded-lg'>
                                                                <FaCrown className='text-yellow-500' size={16} />
                                                            </div>
                                                            <span className='font-medium text-main-font'>Plan Purchases</span>
                                                        </div>
                                                        <span className='text-xl font-bold text-yellow-600'>
                                                            {planCounts.totalPlanPurchases}
                                                        </span>
                                                    </div>
                                                    <div
                                                        onClick={() => setShowReferralPlanHistory(true)}
                                                        className='flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200 cursor-pointer hover:shadow-md transition-all'
                                                    >
                                                        <div className='flex items-center gap-3'>
                                                            <div className='p-2 bg-blue-100 rounded-lg'>
                                                                <FaGift className='text-blue-500' size={16} />
                                                            </div>
                                                            <span className='font-medium text-main-font'>Referral Plans</span>
                                                        </div>
                                                        <span className='text-xl font-bold text-blue-600'>
                                                            {planCounts.totalReferralPlans}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Referral History Modal */}
            {showReferralHistory && (
                <UserReferralHistory
                    userId={id}
                    onClose={() => setShowReferralHistory(false)}
                    userName={userData?.u_name}
                />
            )}

            {/* Referral Plan History Modal */}
            {showReferralPlanHistory && (
                <UserReferralHistoryPlan
                    userId={id}
                    onClose={() => setShowReferralPlanHistory(false)}
                    userName={userData?.u_name}
                />
            )}

            {/* Send Admin Plan Modal */}
            {showSendPlanModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-5 border-b border-main-border bg-gradient-to-r from-yellow-500 to-orange-500 rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <FaGift className="text-white" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Send Admin Plan</h2>
                                    <p className="text-xs text-white/80">Assign plan to user</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowSendPlanModal(false);
                                    setSelectedPlan('');
                                }}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <FaTimes className="text-white" size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">
                            {/* Selected User Info */}
                            <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-yellow-200">
                                <p className="text-sm text-main-subfont mb-1">Selected User</p>
                                <p className="font-semibold text-main-font text-lg">{userData?.u_name}</p>
                            </div>

                            {/* Select Plan */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-semibold text-main-font">
                                    <FaCrown className="text-yellow-500" size={16} />
                                    Select Plan
                                </label>
                                {loadingPlans ? (
                                    <div className="flex items-center justify-center py-4">
                                        <svg className="animate-spin h-6 w-6 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedPlan}
                                        onChange={(e) => setSelectedPlan(e.target.value)}
                                        className="w-full px-4 py-3 border border-main-border rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition-all bg-main-white cursor-pointer"
                                    >
                                        <option value="">-- Choose a Plan --</option>
                                        {plans.map((plan) => (
                                            <option key={plan._id} value={plan._id}>
                                                {plan.p_name} - ₹{plan.p_price} ({plan.p_duration} days)
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Selected Plan Preview */}
                            {selectedPlan && (
                                <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaCrown className="text-yellow-500" size={16} />
                                        <p className="font-semibold text-main-font">
                                            {plans.find(p => p._id === selectedPlan)?.p_name}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-main-subfont">Price:</span>
                                        <span className="font-bold text-yellow-600 flex items-center">
                                            <FaRupeeSign size={12} />
                                            {plans.find(p => p._id === selectedPlan)?.p_price}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1">
                                        <span className="text-main-subfont">Duration:</span>
                                        <span className="font-medium text-main-font">
                                            {plans.find(p => p._id === selectedPlan)?.p_duration} days
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-3 p-5 border-t border-main-border bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={() => {
                                    setShowSendPlanModal(false);
                                    setSelectedPlan('');
                                }}
                                className="px-5 py-2.5 border border-main-border text-main-font rounded-xl hover:bg-gray-100 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendAdminPlan}
                                disabled={!selectedPlan || sendingPlan}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
                            >
                                {sendingPlan ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <FaPaperPlane size={14} />
                                        Send Plan
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View User History Modal */}
            {showUserHistoryModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col transform transition-all duration-300">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-5 border-b border-main-border bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <FaHistory className="text-white" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Purchase History</h2>
                                    <p className="text-sm text-white/80">{userData?.u_name}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowUserHistoryModal(false);
                                    setUserHistoryData([]);
                                }}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <FaTimes className="text-white" size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-auto p-5">
                            {loadingUserHistory ? (
                                <div className="flex items-center justify-center py-10">
                                    <svg className="animate-spin h-10 w-10 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            ) : userHistoryData.length > 0 ? (
                                <div className="space-y-4">
                                    {userHistoryData.map((history) => (
                                        <div
                                            key={history._id}
                                            className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-main-border hover:shadow-md transition-all group"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2.5 bg-yellow-100 rounded-xl">
                                                        <FaCrown className="text-yellow-500" size={18} />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-semibold text-main-font">{history.p_name}</h3>
                                                        <p className="text-xs text-main-subfont font-mono bg-gray-100 px-2 py-0.5 rounded mt-1 inline-block">
                                                            TXN: {history.p_trn}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-main-font flex items-center justify-end">
                                                            <FaRupeeSign size={14} />
                                                            {history.p_price}
                                                        </p>
                                                        <p className="text-xs text-main-subfont">{history.p_duration} days</p>
                                                    </div>
                                                    {getStatusBadge(history.status)}
                                                    <button
                                                        onClick={() => handleDeleteHistory(history._id)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                                        title="Delete"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-3 pt-3 border-t border-main-border flex flex-wrap gap-4 text-sm text-main-subfont">
                                                <div className="flex items-center gap-1.5">
                                                    <FaCalendarAlt size={12} className="text-green-500" />
                                                    <span>Start: {formatDate(history.start_date)}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <FaCalendarAlt size={12} className="text-red-500" />
                                                    <span>End: {formatDate(history.end_date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-10 text-main-subfont">
                                    <div className="p-4 bg-gray-100 rounded-full mb-4">
                                        <FaCrown className="text-gray-300" size={40} />
                                    </div>
                                    <p className="text-lg font-medium text-main-font">No purchase history</p>
                                    <p className="text-sm">This user hasn't purchased any plans yet.</p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end p-5 border-t border-main-border bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={() => {
                                    setShowUserHistoryModal(false);
                                    setUserHistoryData([]);
                                }}
                                className="px-5 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserInfo;